import { makeStyles } from '@material-ui/core/styles';
import type { Entity } from '@backstage/catalog-model';
import { EntityInfoCard, useEntity } from '@backstage/plugin-catalog-react';
import type { LifecycleTone } from './types';

export const PHI_TOUCHING_ANNOTATION = 'hone.health/phi-touching';

export type PhiTouchingStatus = 'phi' | 'no-phi' | 'not-set';

export function getPhiTouchingStatus(entity: Entity): PhiTouchingStatus {
  const raw = entity.metadata.annotations?.[PHI_TOUCHING_ANNOTATION];
  switch (raw?.trim().toLowerCase()) {
    case 'true':
      return 'phi';
    case 'false':
      return 'no-phi';
    default:
      return 'not-set';
  }
}

const PHI_ANNOTATED_KINDS = new Set([
  'repository',
  'component',
  'api',
  'resource',
  'system',
  'domain',
]);

/** The kinds the annotation is required on; every other kind shows no card. */
export function showsPhiScope(entity: Entity): boolean {
  return PHI_ANNOTATED_KINDS.has(entity.kind.toLocaleLowerCase('en-US'));
}

/*
 * PHI scope is a Hone concept the mock has no screen for, so the card borrows
 * the language of the cards beside it in the rail rather than inventing one: the
 * standard surface, and the status trio the pills elsewhere already use — full
 * strength text on a 10% tint of the same hue.
 *
 * Warning for in-scope, because HIPAA review is the state worth noticing.
 * Out-of-scope is a settled fact, not good news, so it reads neutral-positive
 * rather than loud green. Unset stays grey and keeps naming the annotation —
 * that string is the fix, and dropping it would leave nothing to act on.
 */
const TONES: Record<PhiTouchingStatus, LifecycleTone> = {
  phi: { foreground: '#b45309', background: 'rgba(194, 97, 10, 0.1)' },
  'no-phi': { foreground: '#157347', background: 'rgba(24, 154, 92, 0.1)' },
  'not-set': {
    foreground: 'var(--bui-fg-secondary)',
    background: 'var(--hone-portal-fill-inset)',
  },
};

const LABELS: Record<PhiTouchingStatus, string> = {
  phi: 'Handles PHI',
  'no-phi': 'Does not handle PHI',
  'not-set': 'Not set',
};

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 8,
  },
  pill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 7,
    borderRadius: 999,
    padding: '3px 10px',
    fontSize: 12,
    fontWeight: 500,
    lineHeight: 1.5,
  },
  dot: {
    width: 7,
    height: 7,
    borderRadius: 999,
    background: 'currentColor',
    flex: '0 0 auto',
  },
  note: {
    fontSize: 11,
    lineHeight: 1.5,
    color: 'var(--bui-fg-secondary)',
  },
  annotation: {
    fontFamily: 'var(--bui-font-monospace)',
  },
});

export function PhiTouchingCard() {
  const { entity } = useEntity();
  const classes = useStyles();

  if (!showsPhiScope(entity)) {
    return null;
  }

  const status = getPhiTouchingStatus(entity);
  const tone = TONES[status];

  return (
    <EntityInfoCard title="PHI scope">
      <div className={classes.root} aria-label="PHI scope">
        <span
          className={classes.pill}
          style={{ color: tone.foreground, background: tone.background }}
        >
          <span className={classes.dot} />
          {LABELS[status]}
        </span>
        {status === 'phi' && (
          <span className={classes.note}>In HIPAA review scope</span>
        )}
        {status === 'not-set' && (
          <span className={classes.note}>
            Add <span className={classes.annotation}>
              {PHI_TOUCHING_ANNOTATION}
            </span>{' '}
            to this entity.
          </span>
        )}
      </div>
    </EntityInfoCard>
  );
}
